const PENDING_PUSH_PATH_KEY = "pending_notif_path";

function isSafeAppPath(path: string | null | undefined): path is string {
  return !!path && path.startsWith("/") && !path.startsWith("//");
}

export function getPushTargetFromHash(hash: string): string | null {
  const match = hash.match(/[#&]notif=([^&]+)/);
  if (!match) return null;

  try {
    const decoded = decodeURIComponent(match[1]);
    return isSafeAppPath(decoded) ? decoded : null;
  } catch {
    return null;
  }
}

export function getPushTargetFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  const notifPath = params.get("notif_path");
  return isSafeAppPath(notifPath) ? notifPath : null;
}

export function getStoredPushTarget(): string | null {
  try {
    const pending = localStorage.getItem(PENDING_PUSH_PATH_KEY);
    return isSafeAppPath(pending) ? pending : null;
  } catch {
    return null;
  }
}

export function storePushTarget(path: string | null | undefined) {
  if (!isSafeAppPath(path)) return;

  try {
    localStorage.setItem(PENDING_PUSH_PATH_KEY, path);
  } catch {
    // ignore storage failures
  }
}

export function clearStoredPushTarget() {
  try {
    localStorage.removeItem(PENDING_PUSH_PATH_KEY);
  } catch {
    // ignore storage failures
  }
}

const PUSH_ID_PARAM = "onesignal_push_id";

function stripParamFromQuery(raw: string, prefix: "?" | "#"): string {
  const body = raw.startsWith(prefix) ? raw.slice(1) : raw;
  if (!body) return "";

  const params = new URLSearchParams(body);
  if (!params.has(PUSH_ID_PARAM)) return raw;

  params.delete(PUSH_ID_PARAM);
  const next = params.toString();
  return next ? prefix + next : "";
}

/**
 * Removes the OneSignal `onesignal_push_id` parameter from the live address,
 * in both the query string and the hash, so a later app load cannot read the
 * same push id again and record a second open for the same notification.
 * Every other parameter is preserved, and the address is only rewritten when
 * the id was actually present.
 */
export function stripPushIdFromAddress() {
  if (typeof window === "undefined") return;

  try {
    const { pathname, search, hash } = window.location;
    const nextSearch = stripParamFromQuery(search || "", "?");
    const nextHash = stripParamFromQuery(hash || "", "#");

    if (nextSearch === (search || "") && nextHash === (hash || "")) return;

    window.history.replaceState(null, "", pathname + nextSearch + nextHash);
  } catch {
    // ignore history/location failures
  }
}

export function getAnyPushTarget() {
  if (typeof window === "undefined") return null;

  return (
    getPushTargetFromHash(window.location.hash || "") ||
    getPushTargetFromSearch(window.location.search || "") ||
    getStoredPushTarget()
  );
}
